import { CalendarDays } from "lucide-react";
import { EventCard } from "@/components/schedule/EventCard";
import { addDays, eventsOnDay, fmtDay, sameDay, startOfMonth } from "@/lib/schedule";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7);

const dropProps = (onDrop, day, hour = null) => ({
  onDragOver: (e) => { e.preventDefault(); e.currentTarget.classList.add("bg-indigo-50"); },
  onDragLeave: (e) => e.currentTarget.classList.remove("bg-indigo-50"),
  onDrop: (e) => { e.preventDefault(); e.currentTarget.classList.remove("bg-indigo-50"); onDrop?.(e.dataTransfer.getData("text/plain"), day, hour); },
});

export const AgendaList = ({ events, days, onOpen, showEngineer }) => {
  const groups = days.map((d) => ({ d, items: eventsOnDay(events, d) })).filter((g) => g.items.length);
  if (!groups.length) {
    return (
      <div className="text-center text-sm text-slate-500 py-12 bg-white border border-slate-200 rounded-lg" data-testid="agenda-empty">
        <CalendarDays className="w-7 h-7 mx-auto mb-2 text-slate-300" /> Nothing scheduled.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="agenda-list">
      {groups.map(({ d, items }) => (
        <section key={d.toISOString()}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{fmtDay(d)}</div>
          <div className="space-y-2">{items.map((ev) => <EventCard key={ev.id} ev={ev} onOpen={onOpen} showEngineer={showEngineer} />)}</div>
        </section>
      ))}
    </div>
  );
};

export const DayGrid = ({ events, day, onOpen, onDrop, canDrag }) => (
  <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="day-grid">
    {HOURS.map((h) => {
      const slot = (ev) => { const s = new Date(ev.start); return sameDay(s, day) ? Math.min(21, Math.max(7, s.getHours())) : 7; };
      const items = eventsOnDay(events, day).filter((ev) => slot(ev) === h);
      return (
        <div key={h} className="flex min-h-[56px] transition-colors" {...dropProps(onDrop, day, h)} data-testid={`day-slot-${h}`}>
          <div className="w-16 shrink-0 text-[11px] text-slate-400 px-2 pt-2 font-mono">{String(h).padStart(2, "0")}:00</div>
          <div className="flex-1 p-1.5 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((ev) => <EventCard key={ev.id} ev={ev} onOpen={onOpen} draggable={canDrag} />)}
          </div>
        </div>
      );
    })}
  </div>
);

export const WeekGrid = ({ events, start, onOpen, onDrop, canDrag }) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-7 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="week-grid">
      {days.map((d) => (
        <div key={d.toISOString()} className="border-r last:border-r-0 border-slate-100 min-h-[420px] flex flex-col transition-colors" {...dropProps(onDrop, d)} data-testid={`week-col-${d.getDate()}`}>
          <div className={`px-2 py-2 text-xs font-semibold border-b border-slate-100 ${sameDay(d, new Date()) ? "text-indigo-700 bg-indigo-50" : "text-slate-600"}`}>{fmtDay(d)}</div>
          <div className="p-1.5 space-y-1.5 flex-1">
            {eventsOnDay(events, d).map((ev) => <EventCard key={ev.id} ev={ev} onOpen={onOpen} draggable={canDrag} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export const MonthGrid = ({ events, anchor, start, onOpen, onDrop, canDrag }) => {
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const month = startOfMonth(anchor).getMonth();
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="month-grid">
      <div className="grid grid-cols-7 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => (
          <div key={d.toISOString()} className={`min-h-[104px] border-r border-b border-slate-100 p-1 transition-colors ${d.getMonth() !== month ? "bg-slate-50/70" : ""}`} {...dropProps(onDrop, d)}>
            <div className={`text-[11px] mb-1 ${sameDay(d, new Date()) ? "text-indigo-700 font-bold" : "text-slate-500"}`}>{d.getDate()}</div>
            <div className="space-y-1">{eventsOnDay(events, d).slice(0, 4).map((ev) => <EventCard key={ev.id} ev={ev} compact onOpen={onOpen} draggable={canDrag} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TeamGrid = ({ events, engineers, days, onOpen, onDrop, canDrag }) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="team-grid">
    <table className="w-full border-collapse text-sm min-w-[900px] table-fixed">
      <thead>
        <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="text-left px-3 py-2 w-44 border-b border-slate-100">Engineer</th>
          {days.map((d) => <th key={d.toISOString()} className={`text-left px-2 py-2 border-b border-l border-slate-100 ${sameDay(d, new Date()) ? "text-indigo-700" : ""}`}>{fmtDay(d)}</th>)}
        </tr>
      </thead>
      <tbody>
        {engineers.map((u) => {
          const mine = events.filter((ev) => ev.engineer_id === u.id);
          return (
            <tr key={u.id} className="align-top" data-testid={`team-row-${u.id}`}>
              <td className="px-3 py-2 border-b border-slate-100">
                <div className="font-medium text-slate-900">{u.name}</div>
                <div className="text-[11px] text-slate-500">{u.job_title || u.role_name || ""}</div>
              </td>
              {days.map((d) => {
                const items = eventsOnDay(mine, d).filter((ev) => ev.status !== "Cancelled");
                return (
                  <td key={d.toISOString()} className="px-1.5 py-1.5 border-b border-l border-slate-100 min-w-[120px] transition-colors" {...dropProps((id, day) => onDrop?.(id, day, null, u.id), d)} data-testid={`team-cell-${u.id}-${d.getDate()}`}>
                    {items.length ? (
                      <div className="space-y-1">{items.map((ev) => <EventCard key={ev.id} ev={ev} compact onOpen={onOpen} draggable={canDrag} />)}</div>
                    ) : <span className="text-[11px] text-emerald-600" data-testid={`team-free-${u.id}-${d.getDate()}`}>Free</span>}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
